import dayjs, { isDayjs } from "./dayjs";
import type { Dayjs } from "dayjs";

export const DEFAULT_US_DATE_FORMAT = "YYYY-MM-DD hh:mm A";

export const today = (dateFormat: string): string => {
  return dayjs().format(dateFormat);
};

export const formatMillisToMinutesAndSeconds = (ms: number): string => {
  return dayjs.utc(ms).format("mm:ss");
};

export const dateWithFormat = (
  date: string | number | Date,
  format: string,
): string => {
  return dayjs(new Date(date)).format(format);
};

export const dateWithUSDateFormat = (date: string | number | Date): string => {
  return dateWithFormat(date, DEFAULT_US_DATE_FORMAT);
};

export const utcDateWithFormat = (
  date: string | number,
  format: string,
): string => {
  return dayjs.utc(date).format(format);
};

export const utcDateWithDefaultUSDateFormat = (
  date: string | number,
): string => {
  return utcDateWithFormat(date, DEFAULT_US_DATE_FORMAT);
};

export const dateAtTimezone = (
  date: string | Dayjs,
  timezone: string,
  format: string = DEFAULT_US_DATE_FORMAT,
): string => {
  if (!date) return "";
  const base = isDayjs(date)
    ? (date as Dayjs).clone()
    : dayjs.utc(date as string);
  if (!base.isValid()) return "";

  // Check if timezone is valid
  let zoned;
  try {
    zoned = timezone ? base.tz(timezone) : base.local();
  } catch {
    zoned = base.local();
  }
  return zoned.isValid() ? zoned.format(format) : "";
};

export const isDateValid = (
  date: string | number | Date | null | undefined,
): boolean => {
  return dayjs(date).isValid();
};

/**
 * Parse a time string in multiple common formats and format it for display.
 * Accepts 12h and 24h times and ISO datetimes.
 */
export const formatTimeString = (
  val?: string,
  outputFormat: string = "h:mm a",
): string => {
  if (!val) return "";
  const formats = [
    "h:mm A",
    "hh:mm A",
    "h:mm a",
    "hh:mm a",
    "H:mm",
    "HH:mm",
    "HH:mm:ss",
  ];
  let parsed = dayjs(val, formats, true);
  if (!parsed.isValid()) parsed = dayjs(val);
  if (!parsed.isValid()) return "";
  return parsed.format(outputFormat);
};

/**
 * Convert a date string from UTC to the user's timezone
 * Handles both ISO strings (2024-06-27T21:58:06.809Z) and formatted strings
 */
export const convertUtcToTimezone = (
  dateString: string | null | undefined,
  targetTimezone: string | null | undefined,
): string | null | undefined => {
  if (!dateString) return dateString;

  // If targetTimezone isn't provided yet (settings not loaded), fall back to user's local timezone
  const tz =
    targetTimezone || (dayjs && dayjs.tz ? dayjs.tz.guess() : undefined);

  // Try parsing as ISO first, then as formatted strings (flexible & default)
  let utcDate = null;

  if (dateString.includes("T") || dateString.includes("Z")) {
    utcDate = dayjs.utc(dateString);
  } else {
    // Try flexible format (single-digit hour) first, then default
    utcDate = dayjs.utc(dateString, "YYYY-MM-DD h:mm A");
    if (!utcDate.isValid()) {
      utcDate = dayjs.utc(dateString, DEFAULT_US_DATE_FORMAT);
    }
  }

  if (!utcDate || !utcDate.isValid()) return dateString;

  // Convert to target timezone (fallback to local guess if tz is undefined)
  return tz
    ? utcDate.tz(tz).format(DEFAULT_US_DATE_FORMAT)
    : utcDate.local().format(DEFAULT_US_DATE_FORMAT);
};
